// src/theme/themeConfig.js
// ─── Color Skin Theme System — National Biodiversity Authority ───────────────
// Single source of truth for ALL dashboard colours.
// To add a new skin: define a token object inside THEMES, restart dev server.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════════
   TOKEN SCHEMA — every theme MUST supply every key listed in the base theme.
   ─────────────────────────────────────────────────────────────────────────────
   Naming convention:
     • section_property   e.g. sidebar, govBar, statCard
     • gradient / glow    for multi-stop colours & box-shadow colours
     • Active / Hover     for interactive state variants
═══════════════════════════════════════════════════════════════════════════════ */

/* ──────────────────────── PALETTE BUILDING HELPERS ──────────────────────── */
const grad = (deg, ...stops) => `linear-gradient(${deg}deg, ${stops.join(', ')})`;
const glow = (r, g, b, a = 0.35) => `rgba(${r},${g},${b},${a})`;

/* ═══════════════════════════════════════════════
   THEME 1 — Green & Blue  (DEFAULT)
   Matches the NBA-suggested design exactly.
═══════════════════════════════════════════════ */
const greenBlue = {
  id: 'greenBlue',
  name: 'Green & Blue',
  description: 'Default — NBA Official',
  preview: ['#064d2c', '#0f8248', '#0d5bd7'],

  // ── Top bars ──
  navbarBg: grad(90, '#003b8f 0%', '#0d5bd7 45%', '#1e78ff 100%'),
  govBar: grad(90, '#1B3C87 0%', '#1860CA 100%'),
  triStripe: ['#FF9933', '#FFFFFF', '#138808'],

  // ── Sidebar ──
  sidebar: grad(180, '#064d2c 0%', '#0b6b3d 40%', '#0f8248 75%', '#12a15a 100%'),
  sidebarActive: 'linear-gradient(90deg, rgba(255,255,255,0.16), rgba(255,255,255,0.08))',
  sidebarHover: 'rgba(255,255,255,0.12)',
  sidebarAccent: grad(180, '#7dffbc', '#ffffff'),
  sidebarAccentGlow: glow(125, 255, 188, 0.6),
  sidebarText: 'rgba(255,255,255,0.88)',
  sidebarTextActive: '#FFFFFF',
  sidebarIconColor: 'rgba(255,255,255,0.75)',
  sidebarIconActive: '#FFFFFF',
  sidebarDivider: 'rgba(255,255,255,0.12)',

  // ── Main content ──
  mainBg: '#F5F7FB',
  cardBg: '#FFFFFF',

  // ── Brand / accent ──
  primaryColor: '#2E7D32',
  primaryLight: 'rgba(46,125,50,0.07)',
  accentGradient: grad(135, '#1565C0', '#1E88E5'),
  accentGlow: glow(21, 101, 192, 0.28),
  accentHoverGlow: glow(21, 101, 192, 0.40),

  // ── Typography ──
  titleColor: '#1B5E20',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  textFaint: '#94A3B8',

  // ── Borders ──
  borderColor: '#E2E8F0',
  borderLight: '#F1F5F9',
  hoverBg: '#F8FAFC',

  // ── Tags / badges ──
  tagBg: 'rgba(46,125,50,0.08)',
  tagColor: '#2E7D32',

  // ── Pagination ──
  paginationBg: grad(135, '#1B5E20', '#2E7D32'),
  paginationGlow: glow(27, 94, 32, 0.25),

  // ── Stat cards ──
  statCards: [
    { gradient: grad(135, '#3B82F6 0%', '#6366F1 100%'), glow: glow(59, 130, 246) },
    { gradient: grad(135, '#8B5CF6 0%', '#D946EF 100%'), glow: glow(139, 92, 246) },
    { gradient: grad(135, '#10B981 0%', '#34D399 100%'), glow: glow(16, 185, 129) },
    { gradient: grad(135, '#F59E0B 0%', '#FB923C 100%'), glow: glow(245, 158, 11) },
  ],

  // ── Donut chart ──
  donutColors: ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B'],

  // ── Toggle button ──
  toggleBtnBg: grad(135, '#1B5E20', '#2E7D32'),
  toggleBtnShadow: '0 4px 16px ' + glow(27, 94, 32),
};

/* ═══════════════════════════════════════════════
   THEME 2 — Deep Navy
   Classic government-style dark navy sidebar.
═══════════════════════════════════════════════ */
const deepNavy = {
  id: 'deepNavy',
  name: 'Deep Navy',
  description: 'Classic government blue',
  preview: ['#0B1D3A', '#0F2A5F', '#1E4FA3'],

  govBar: grad(90, '#0a1f3c', '#0d3460'),
  navbarBg: grad(90, '#0a1f3c 0%', '#0d3460 45%', '#1a4a8a 100%'),
  triStripe: ['#FF9933', '#FFFFFF', '#138808'],

  sidebar: grad(180, '#0B1D3A 0%', '#0F2A5F 50%', '#1E4FA3 100%'),
  sidebarActive: 'linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))',
  sidebarHover: 'rgba(255,255,255,0.08)',
  sidebarAccent: grad(180, '#3B82F6', '#6366F1'),
  sidebarAccentGlow: glow(59, 130, 246, 0.7),
  sidebarText: 'rgba(255,255,255,0.60)',
  sidebarTextActive: '#FFFFFF',
  sidebarIconColor: 'rgba(255,255,255,0.45)',
  sidebarIconActive: '#FFFFFF',
  sidebarDivider: 'rgba(255,255,255,0.07)',

  mainBg: '#F5F7FB',
  cardBg: '#FFFFFF',

  primaryColor: '#1E4FA3',
  primaryLight: 'rgba(30,79,163,0.06)',
  accentGradient: grad(135, '#3B82F6', '#6366F1'),
  accentGlow: glow(59, 130, 246, 0.28),
  accentHoverGlow: glow(59, 130, 246, 0.38),

  titleColor: '#0a1f3c',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  textFaint: '#94A3B8',

  borderColor: '#E2E8F0',
  borderLight: '#F1F5F9',
  hoverBg: '#F8FAFC',

  tagBg: 'rgba(59,130,246,0.08)',
  tagColor: '#3B82F6',

  paginationBg: grad(135, '#0F2A5F', '#1E4FA3'),
  paginationGlow: glow(15, 42, 95, 0.25),

  statCards: [
    { gradient: grad(135, '#3B82F6 0%', '#6366F1 100%'), glow: glow(59, 130, 246) },
    { gradient: grad(135, '#8B5CF6 0%', '#D946EF 100%'), glow: glow(139, 92, 246) },
    { gradient: grad(135, '#10B981 0%', '#34D399 100%'), glow: glow(16, 185, 129) },
    { gradient: grad(135, '#F59E0B 0%', '#FB923C 100%'), glow: glow(245, 158, 11) },
  ],

  donutColors: ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B'],

  toggleBtnBg: grad(135, '#0F2A5F', '#1E4FA3'),
  toggleBtnShadow: '0 4px 16px ' + glow(15, 42, 95),
};

/* ═══════════════════════════════════════════════
   THEME 3 — Saffron Pride
   Bold Indian tricolour-inspired palette.
═══════════════════════════════════════════════ */
const saffronPride = {
  id: 'saffronPride',
  name: 'Saffron Pride',
  description: 'Indian tricolour inspired',
  preview: ['#E65100', '#F57C00', '#2E7D32'],

  govBar: grad(90, '#BF360C', '#D84315'),
  navbarBg: grad(90, '#BF360C 0%', '#D84315 45%', '#E64A19 100%'),
  triStripe: ['#FF9933', '#FFFFFF', '#138808'],

  sidebar: grad(180, '#3E2723 0%', '#4E342E 50%', '#5D4037 100%'),
  sidebarActive: 'rgba(255,200,100,0.15)',
  sidebarHover: 'rgba(255,200,100,0.06)',
  sidebarAccent: grad(180, '#FF9800', '#F57C00'),
  sidebarAccentGlow: glow(255, 152, 0, 0.7),
  sidebarText: 'rgba(255,255,255,0.60)',
  sidebarTextActive: '#FFE0B2',
  sidebarIconColor: 'rgba(255,255,255,0.45)',
  sidebarIconActive: '#FFE0B2',
  sidebarDivider: 'rgba(255,255,255,0.08)',

  mainBg: '#FAF5F0',
  cardBg: '#FFFFFF',

  primaryColor: '#E65100',
  primaryLight: 'rgba(230,81,0,0.06)',
  accentGradient: grad(135, '#FF9800', '#F57C00'),
  accentGlow: glow(255, 152, 0, 0.28),
  accentHoverGlow: glow(255, 152, 0, 0.38),

  titleColor: '#3E2723',
  textPrimary: '#1B1B1B',
  textSecondary: '#5D4037',
  textMuted: '#795548',
  textFaint: '#A1887F',

  borderColor: '#D7CCC8',
  borderLight: '#EFEBE9',
  hoverBg: '#FFF8E1',

  tagBg: 'rgba(255,152,0,0.08)',
  tagColor: '#E65100',

  paginationBg: grad(135, '#BF360C', '#E65100'),
  paginationGlow: glow(191, 54, 12, 0.25),

  statCards: [
    { gradient: grad(135, '#FF9800 0%', '#F57C00 100%'), glow: glow(255, 152, 0) },
    { gradient: grad(135, '#E65100 0%', '#BF360C 100%'), glow: glow(230, 81, 0) },
    { gradient: grad(135, '#2E7D32 0%', '#43A047 100%'), glow: glow(46, 125, 50) },
    { gradient: grad(135, '#FDD835 0%', '#FBC02D 100%'), glow: glow(253, 216, 53) },
  ],

  donutColors: ['#FF9800', '#E65100', '#2E7D32', '#FDD835'],

  toggleBtnBg: grad(135, '#BF360C', '#E65100'),
  toggleBtnShadow: '0 4px 16px ' + glow(191, 54, 12),
};

/* ═══════════════════════════════════════════════
   THEME 4 — Teal & Purple
   Modern, fresh aesthetic for a contemporary feel.
═══════════════════════════════════════════════ */
const tealPurple = {
  id: 'tealPurple',
  name: 'Teal & Purple',
  description: 'Modern elegant look',
  preview: ['#004D40', '#00695C', '#6A1B9A'],

  govBar: grad(90, '#004D40', '#00695C'),
  navbarBg: grad(90, '#004D40 0%', '#00695C 45%', '#00897B 100%'),
  triStripe: ['#FF9933', '#FFFFFF', '#138808'],

  sidebar: grad(180, '#1A1A2E 0%', '#16213E 50%', '#0F3460 100%'),
  sidebarActive: 'rgba(0,230,180,0.12)',
  sidebarHover: 'rgba(0,230,180,0.05)',
  sidebarAccent: grad(180, '#00BFA5', '#1DE9B6'),
  sidebarAccentGlow: glow(0, 191, 165, 0.7),
  sidebarText: 'rgba(255,255,255,0.60)',
  sidebarTextActive: '#A7FFEB',
  sidebarIconColor: 'rgba(255,255,255,0.45)',
  sidebarIconActive: '#A7FFEB',
  sidebarDivider: 'rgba(255,255,255,0.08)',

  mainBg: '#F0F7F5',
  cardBg: '#FFFFFF',

  primaryColor: '#00695C',
  primaryLight: 'rgba(0,105,92,0.06)',
  accentGradient: grad(135, '#00BFA5', '#1DE9B6'),
  accentGlow: glow(0, 191, 165, 0.28),
  accentHoverGlow: glow(0, 191, 165, 0.38),

  titleColor: '#004D40',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  textFaint: '#94A3B8',

  borderColor: '#E0F2F1',
  borderLight: '#E0F2F1',
  hoverBg: '#E0F2F1',

  tagBg: 'rgba(0,191,165,0.08)',
  tagColor: '#00695C',

  paginationBg: grad(135, '#004D40', '#00695C'),
  paginationGlow: glow(0, 77, 64, 0.25),

  statCards: [
    { gradient: grad(135, '#00BFA5 0%', '#1DE9B6 100%'), glow: glow(0, 191, 165) },
    { gradient: grad(135, '#7C4DFF 0%', '#B388FF 100%'), glow: glow(124, 77, 255) },
    { gradient: grad(135, '#26A69A 0%', '#80CBC4 100%'), glow: glow(38, 166, 154) },
    { gradient: grad(135, '#FFB300 0%', '#FFD54F 100%'), glow: glow(255, 179, 0) },
  ],

  donutColors: ['#00BFA5', '#7C4DFF', '#26A69A', '#FFB300'],

  toggleBtnBg: grad(135, '#004D40', '#00695C'),
  toggleBtnShadow: '0 4px 16px ' + glow(0, 77, 64),
};

/* ═══════════════════════════════════════════════
   REGISTRY — order here = order in the picker.
═══════════════════════════════════════════════ */
const THEMES = {
  greenBlue,
  deepNavy,
  saffronPride,
  tealPurple,
};

const THEME_LIST  = Object.values(THEMES);
const STORAGE_KEY = 'nba-color-skin';
const DEFAULT_THEME = 'greenBlue';

/* ═══════════════════════════════════════════════
   CONTEXT + PROVIDER
═══════════════════════════════════════════════ */
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved && THEMES[saved] ? saved : DEFAULT_THEME;
    } catch { return DEFAULT_THEME; }
  });

  const currentTheme = useMemo(() => THEMES[themeId] || THEMES[DEFAULT_THEME], [themeId]);

  const switchTheme = useCallback((id) => {
    if (!THEMES[id]) return;
    setThemeId(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
  }, [themeId]);

  const ctx = useMemo(() => ({
    theme: currentTheme,
    themeId,
    themes: THEME_LIST,
    switchTheme,
  }), [currentTheme, themeId, switchTheme]);

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>;
}

export function useColorSkin() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useColorSkin must be used within ThemeProvider');
  return ctx;
}

export { THEMES, THEME_LIST, DEFAULT_THEME };
